import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getHobbies, getProvinces, getCities } from "../../api/apiService";
import {
  Button,
  Dropdown,
  InputField,
  SelectField,
  SkeletonField,
  CheckboxField,
  SelectEventField,
} from "../../components";
import { useSubcriptionForm } from "../hooks/useSubcriptionForm";
import { useServiceData } from "../../context/ServiceContext";
import { RECAPTCHA_SITE_KEY } from "../../constants/config";
import ReCAPTCHA from "react-google-recaptcha";
import type { SubcriptionFormValues } from "../../types/form.types";
import type { FormikHelpers } from "formik";
import styles from "../MainForm/Form.module.css";
import "../../styles/animations.css";

interface SubcriptionFormProps {
  onSubmit: (
    values: SubcriptionFormValues,
    formikHelpers: FormikHelpers<any>
  ) => void;
  subcriptionReCaptcha: string | null;
  onRecaptchaChange: (token: string | null) => void;
}

export const SubcriptionForm: React.FC<SubcriptionFormProps> = ({
  onSubmit,
  subcriptionReCaptcha,
  onRecaptchaChange,
}) => {
  const { t } = useTranslation();
  const { setHobbies, setDepartments, setCities } = useServiceData();
  const [currentProvice, setCurrentProvice] = React.useState<string | null>(
    null
  );

  const { data: hobbies, isLoading: isLoadingHobbies } = useQuery({
    queryKey: ["hobbies"],
    queryFn: getHobbies,
  });

  const { data: provinces, isLoading: isLoadingProvinces } = useQuery({
    queryKey: ["departments"],
    queryFn: getProvinces,
  });

  const { data: cities, isLoading: isLoadingCities } = useQuery({
    queryKey: ["department", currentProvice],
    queryFn: () => getCities(Number(currentProvice)),
    enabled: !!currentProvice,
  });

  React.useEffect(() => {
    if (hobbies) setHobbies(hobbies);
  }, [hobbies, setHobbies]);

  React.useEffect(() => {
    if (provinces) setDepartments(provinces);
  }, [provinces, setDepartments]);

  React.useEffect(() => {
    if (cities) setCities(cities);
  }, [cities, setCities]);

  const { formik } = useSubcriptionForm(onSubmit);

  return (
    <form onSubmit={formik.handleSubmit} className="fadeIn">
      <div className={styles.formRow}>
        <div className={styles.formColumn}>
          {hobbies && (
            <Dropdown
              label={t("field.hobbies")}
              id="hobbies"
              name="hobbies"
              options={hobbies}
              setFieldValue={formik.setFieldValue}
              getFieldProps={formik.getFieldProps}
              touched={formik.touched}
              errors={formik.errors}
            />
          )}

          {isLoadingHobbies && <SkeletonField />}

          <InputField
            label={t("field.email")}
            id="email"
            name="email"
            placeholder={t("field.email.placeholder")}
            type="email"
            getFieldProps={formik.getFieldProps}
            touched={formik.touched}
            errors={formik.errors}
          />

          <InputField
            label={t("field.phone")}
            id="phone"
            name="phone"
            placeholder={t("field.phone.placeholder")}
            type="tel"
            getFieldProps={formik.getFieldProps}
            touched={formik.touched}
            errors={formik.errors}
          />

          {provinces && (
            <SelectEventField
              label={t("field.department")}
              id="department"
              name="department"
              options={provinces}
              getFieldProps={formik.getFieldProps}
              onCurrentProvice={setCurrentProvice}
              touched={formik.touched}
              errors={formik.errors}
            />
          )}

          {isLoadingProvinces && <SkeletonField />}

          {cities && (
            <SelectField
              label={t("field.city")}
              id="city"
              name="city"
              options={cities}
              getFieldProps={formik.getFieldProps}
              touched={formik.touched}
              errors={formik.errors}
            />
          )}

          {isLoadingCities && <SkeletonField />}

          <InputField
            label={t("field.address")}
            id="address"
            name="address"
            placeholder={t("field.address.placeholder")}
            type="text"
            getFieldProps={formik.getFieldProps}
            touched={formik.touched}
            errors={formik.errors}
          />

          <ReCAPTCHA
            className={styles.recaptchaContainer}
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={onRecaptchaChange}
          />

          <CheckboxField
            id="terminos-condiciones-club-osi"
            name="acceptTermsAndConditions"
            getFieldProps={formik.getFieldProps}
            touched={formik.touched}
            errors={formik.errors}
          >
            <span>
              {t("field.acceptTermsAndConditions")}
              <a
                href="https://www.clubosi.com/es/web/guest/terminos-y-condiciones"
                rel="noreferrer"
                target="_blank"
              >
                {t("field.acceptTermsAndConditions.link")}
              </a>
            </span>
          </CheckboxField>

          <div className={styles.justifyContentCenter}>
            <Button
              type="submit"
              disabled={
                !formik.isValid || !formik.dirty || !subcriptionReCaptcha
              }
            >
              {formik.isSubmitting
                ? t("form.button.subcription.sending")
                : t("form.button.subcription")}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};
